import { Plus, X, Star, Gauge } from "lucide-react";
import { gruppoColor } from "@/lib/race";
import PilotAvatar from "@/components/PilotAvatar";

function ClassBadge({ pilot, gc, classCount, classLimit }) {
  return (
    <span
      className="font-body-sport text-[10px] font-bold tracking-widest px-1.5 py-0.5 rounded border"
      style={{ color: gc, borderColor: `${gc}55`, background: `${gc}18` }}
      data-testid={`driver-group-badge-${pilot.id}`}
    >
      {pilot.gruppo}
      {classCount > 0 && (
        <span
          data-testid={`driver-class-count-${pilot.id}`}
          className={`ml-1.5 font-time ${classCount >= classLimit ? "text-[#FF1E27]" : ""}`}
        >
          · {classCount}/{classLimit}
        </span>
      )}
    </span>
  );
}

function CaptainBadge({ pilot }) {
  return (
    <span
      data-testid={`captain-badge-${pilot.id}`}
      className="font-body-sport text-[10px] font-bold tracking-widest px-1.5 py-0.5 rounded text-black"
      style={{ background: "var(--brand-badge, #FFB703)" }}
    >
      CAPTAIN 1.5X
    </span>
  );
}

function DriverStats({ pilot, punti }) {
  return (
    <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-[#3F3F4C]/60">
      <div className="flex flex-col">
        <span className="text-[9px] tracking-widest text-gray-500 font-body-sport">PUNTI</span>
        <span className="font-time font-bold text-white text-sm">{punti}</span>
      </div>
      <div className="flex flex-col">
        <span className="text-[9px] tracking-widest text-gray-500 font-body-sport">PREZZO</span>
        <span className="font-time font-bold text-white text-sm">{pilot.prezzo}</span>
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-[9px] tracking-widest text-gray-500 font-body-sport">CATEGORIA</span>
        <span className="font-time font-bold text-white text-[10px] truncate" title={pilot.categoria || pilot.gruppo}>
          {pilot.categoria || pilot.gruppo}
        </span>
      </div>
    </div>
  );
}

function EventChips({ pilot }) {
  if (!pilot.eventi || pilot.eventi.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-3" data-testid={`driver-events-${pilot.id}`}>
      {pilot.eventi.map((e, i) => (
        <span
          key={e.id || i}
          title={e.note || e.nome}
          className={`text-[10px] font-body-sport font-bold px-1.5 py-0.5 rounded border ${
            e.punti >= 0
              ? "text-[#22C55E] border-[#22C55E]/40 bg-[#22C55E]/10"
              : "text-[#EF4444] border-[#EF4444]/40 bg-[#EF4444]/10"
          }`}
        >
          {e.icona} {e.nome} {e.punti >= 0 ? `+${e.punti}` : e.punti}
        </span>
      ))}
    </div>
  );
}

function CardActions({ pilot, selected, isCaptain, onAdd, onRemove, onCaptain }) {
  if (!onAdd && !onRemove && !(selected && onCaptain)) return null;
  return (
    <div className="flex gap-2 mt-4">
      {!selected ? (
        onAdd && (
          <button
            data-testid={`driver-add-${pilot.id}`}
            onClick={onAdd}
            className="flex-1 flex items-center justify-center gap-1.5 rounded-lg bg-[var(--brand-primary)] hover:bg-[#CC1118] transition-colors py-2 min-h-[48px] font-body-sport font-bold text-xs tracking-widest text-white uppercase"
          >
            <Plus className="w-4 h-4" /> Aggiungi
          </button>
        )
      ) : (
        <>
          {onCaptain && (
            <button
              data-testid={`driver-captain-toggle-${pilot.id}`}
              onClick={onCaptain}
              title="Imposta come Capitano"
              className={`flex-1 flex items-center justify-center gap-1.5 rounded-lg border py-2 min-h-[48px] font-body-sport font-bold text-xs tracking-widest uppercase transition-colors ${
                isCaptain
                  ? "bg-[#FFB703] text-black border-[#FFB703]"
                  : "border-[#FFB703]/50 text-[#FFB703] hover:bg-[#FFB703]/10"
              }`}
            >
              <Star className={`w-4 h-4 ${isCaptain ? "fill-black" : ""}`} />
              Capitano
            </button>
          )}
          {onRemove && (
            <button
              data-testid={`driver-remove-${pilot.id}`}
              onClick={onRemove}
              title="Rimuovi dalla squadra"
              className="flex items-center justify-center rounded-lg border border-[#3F3F4C] px-4 min-h-[48px] text-gray-400 hover:text-[#FF1E27] hover:border-[#FF1E27] transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </>
      )}
    </div>
  );
}

export default function DriverCard({
  pilot,
  selected = false,
  isCaptain = false,
  onAdd,
  onRemove,
  onCaptain,
  compact = false,
  classCount = 0,
  classLimit = 2,
}) {
  const gc = gruppoColor(pilot.gruppo);
  const punti = pilot.punti_totali ?? pilot.risultato?.punti_totali ?? 0;

  return (
    <div
      data-testid={`driver-card-${pilot.id}`}
      className={`driver-card relative rounded-xl border border-[#3F3F4C] bg-[#2A2A32] overflow-hidden ${
        isCaptain ? "captain-card" : ""
      }`}
    >
      <div className="absolute top-0 left-0 right-0 h-1" style={{ background: gc }} />
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <PilotAvatar pilot={pilot} size={44} />
            <span className="font-racing font-black text-3xl leading-none shrink-0" style={{ color: gc }}>
              {pilot.numero}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <ClassBadge pilot={pilot} gc={gc} classCount={classCount} classLimit={classLimit} />
                {isCaptain && <CaptainBadge pilot={pilot} />}
              </div>
              <h3 className="font-body-sport font-semibold text-white text-base leading-tight truncate mt-1">
                {pilot.nome} {pilot.cognome}
              </h3>
              <p className="text-xs text-gray-400 truncate flex items-center gap-1">
                <Gauge className="w-3 h-3 shrink-0" /> {pilot.vettura}
              </p>
            </div>
          </div>
          <span
            data-testid={`driver-price-${pilot.id}`}
            className="shrink-0 font-time font-bold text-sm px-2 py-1 rounded-full bg-[#FFB703]/15 text-[#FFB703] border border-[#FFB703]/40"
          >
            {pilot.prezzo} CR
          </span>
        </div>

        {!compact && <DriverStats pilot={pilot} punti={punti} />}
        <EventChips pilot={pilot} />
        <CardActions
          pilot={pilot}
          selected={selected}
          isCaptain={isCaptain}
          onAdd={onAdd}
          onRemove={onRemove}
          onCaptain={onCaptain}
        />
      </div>
    </div>
  );
}
